import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, FormControlLabel, CircularProgress, Alert, TextField,
  Autocomplete, Divider, Switch, Grid
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../services/api';

const BulkSubjectAllocationDialog = ({ open, onClose, onSuccess }) => {
  const [teachers, setTeachers] = useState([]);
  const [subjectPapers, setSubjectPapers] = useState([]);
  const [terms, setTerms] = useState([]);

  const [teacher, setTeacher] = useState(null);
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [allPrimaryAllocations, setAllPrimaryAllocations] = useState([]);
  const [settings, setSettings] = useState({
    academic_year: new Date().getFullYear(),
    term_id: '',
    start_date: '',
    end_date: '',
    is_primary: true,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('/report-card/teachers/active'),
      api.get('/report-card/class-stream-papers', { params: { per_page: 500 } }),
      api.get('/academic/terms'),
    ]).then(([t, sp, tr]) => {
      setTeachers(Array.isArray(t.data) ? t.data : []);
      setSubjectPapers(Array.isArray(sp.data?.data) ? sp.data.data : Array.isArray(sp.data) ? sp.data : []);
      const termsData = Array.isArray(tr.data?.data) ? tr.data.data : Array.isArray(tr.data) ? tr.data : [];
      setTerms(termsData);
      if (termsData.length > 0) {
        const today = new Date();
        const current = termsData.find(t => new Date(t.start_date) <= today && new Date(t.end_date) >= today) || termsData[0];
        setSettings(s => ({ ...s, term_id: current.id, start_date: current.start_date || s.start_date, end_date: current.end_date || s.end_date }));
      }
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!teacher) { setExistingAllocations([]); return; }
    const params = { teacher_id: teacher.id, academic_year: settings.academic_year, per_page: 500 };
    if (settings.term_id) params.term_id = settings.term_id;
    api.get('/report-card/subject-teacher-allocations', { params })
      .then(res => setExistingAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [teacher, settings.academic_year, settings.term_id]);

  useEffect(() => {
    const params = { academic_year: settings.academic_year, is_primary: 1, per_page: 500 };
    if (settings.term_id) params.term_id = settings.term_id;
    api.get('/report-card/subject-teacher-allocations', { params })
      .then(res => setAllPrimaryAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [settings.academic_year, settings.term_id]);

  // Clear stale selections when options change
  useEffect(() => {
    const assignedIds = new Set(existingAllocations.map(a => a.class_stream_subject_paper_id));
    setSelectedPapers(prev => prev.filter(p => !assignedIds.has(p.id)));
  }, [existingAllocations]);

  const availablePapers = subjectPapers.filter(
    sp => !existingAllocations.some(a => a.class_stream_subject_paper_id === sp.id)
  );
  const allAssigned = teacher && subjectPapers.length > 0 && availablePapers.length === 0;
  const primaryPaperIds = new Set(allPrimaryAllocations.map(a => a.class_stream_subject_paper_id));
  const primaryConflicts = settings.is_primary ? selectedPapers.filter(sp => primaryPaperIds.has(sp.id)) : [];

  const getPaperLabel = (sp) => {
    const cls = sp.class?.name || sp.class?.class_name || '';
    const stream = sp.stream?.name || sp.stream?.stream_name || '';
    const subject = sp.subject_paper?.subject?.subject_name || '';
    const paper = sp.subject_paper?.paper_name || '';
    return `${cls}${stream ? ` — ${stream}` : ''} · ${subject} (${paper})`;
  };

  const handleSubmit = async () => {
    if (!teacher) { setError('Select a teacher'); return; }
    if (selectedPapers.length === 0) { setError('Select at least one subject paper'); return; }
    if (!settings.start_date) { setError('Start date is required'); return; }
    setError('');
    setLoading(true);
    let created = 0, failed = 0;
    for (const sp of selectedPapers) {
      try {
        await api.post('/report-card/subject-teacher-allocations', {
          teacher_id: teacher.id,
          class_stream_subject_paper_id: sp.id,
          academic_year: settings.academic_year,
          term_id: settings.term_id || null,
          start_date: settings.start_date,
          end_date: settings.end_date || null,
          is_primary: settings.is_primary,
          is_active: settings.is_active,
        });
        created++;
      } catch { failed++; }
    }
    setLoading(false);
    onSuccess(`${created} allocation(s) created${failed > 0 ? `, ${failed} failed/skipped` : ''}`, created > 0 ? 'success' : 'error');
    if (created > 0) handleClose();
  };

  const handleClose = () => {
    setTeacher(null);
    setSelectedPapers([]);
    setSettings({ academic_year: new Date().getFullYear(), term_id: '', start_date: '', end_date: '', is_primary: true, is_active: true });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Subject Teacher Allocation</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {allAssigned && <Alert severity="warning" sx={{ mb: 2 }}>All subject papers have already been assigned to this teacher for the selected year/term.</Alert>}
          {primaryConflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {primaryConflicts.length} selected paper(s) already have a primary teacher: {primaryConflicts.map(sp => getPaperLabel(sp)).join(', ')}. These will be created as secondary assignments.
            </Alert>
          )}

          <Autocomplete
            fullWidth
            options={teachers}
            value={teacher}
            onChange={(_, v) => setTeacher(v)}
            getOptionLabel={(o) => `${o.full_name || `${o.last_name} ${o.first_name}`} (${o.staff_number})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} margin="normal" label="Teacher" required />}
          />

          <Autocomplete
            multiple
            fullWidth
            options={availablePapers}
            value={selectedPapers}
            onChange={(_, v) => setSelectedPapers(v)}
            getOptionLabel={getPaperLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} margin="normal" label="Subject Papers (Class · Stream · Subject)" required />}
            renderTags={(value, getTagProps) =>
              value.map((o, i) => <Chip key={o.id} label={getPaperLabel(o)} size="small" {...getTagProps({ index: i })} />)
            }
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Settings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Academic Year" value={settings.academic_year}
                onChange={(e) => setSettings(s => ({ ...s, academic_year: e.target.value }))}
                inputProps={{ min: 2020, max: new Date().getFullYear() }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={terms}
                value={terms.find(t => t.id === settings.term_id) || null}
                onChange={(_, v) => setSettings(s => ({ ...s, term_id: v?.id || '', start_date: v?.start_date || s.start_date, end_date: v?.end_date || s.end_date }))}
                getOptionLabel={(o) => o.term_name || o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Term (optional)" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker label="Start Date" value={settings.start_date ? dayjs(settings.start_date) : null}
                  onChange={(v) => setSettings(s => ({ ...s, start_date: v ? v.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: { fullWidth: true, required: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker label="End Date (optional)" value={settings.end_date ? dayjs(settings.end_date) : null}
                  onChange={(v) => setSettings(s => ({ ...s, end_date: v ? v.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: { fullWidth: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={settings.is_primary} onChange={(e) => setSettings(s => ({ ...s, is_primary: e.target.checked }))} />} label="Primary Subject Teacher" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={settings.is_active} onChange={(e) => setSettings(s => ({ ...s, is_active: e.target.checked }))} />} label="Active" />
            </Grid>
          </Grid>

          {selectedPapers.length > 0 && teacher && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>{selectedPapers.length} allocation(s)</strong> will be created for {teacher.full_name || `${teacher.last_name} ${teacher.first_name}`}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !teacher || !selectedPapers.length || allAssigned}
          startIcon={loading ? <CircularProgress size={20} /> : null}>
          {loading ? 'Creating...' : `Create ${selectedPapers.length} Allocation(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkSubjectAllocationDialog;
